/**
 * SmsConsentCheckbox
 *
 * Reusable opt-in for SMS + phone-call communication. Used on:
 *  - /agreements/get-started?type=buyer (signup flow)
 *  - The in-chat intake form, next to the phone field
 *  - The open house sign-in sheet (with the server's `consent.text`)
 *
 * The wording is carrier-compliant (10DLC / TCPA): includes the
 * "consent is not a condition of purchase" disclaimer, the STOP/HELP
 * opt-out keywords, and links to Privacy Policy + Terms. Keep edits to
 * the copy here so every surface stays in sync.
 *
 * `text` overrides the fixed copy with wording the caller got from the
 * server (the open house backend snapshots `CONSENT_TEXTS[version]` on
 * every sign-in, so the visitor must see exactly that string). The
 * "Privacy Policy" / "Terms" phrases inside it become links; when the
 * text has neither, the standard "See our … " sentence is appended.
 *
 * WHY the links never navigate the current page: the checkbox sits
 * inside forms whose state lives only in memory (the open-house sign-in
 * sheet, the agreement signup, the chat intake). In phone mode the
 * policies open in a new tab so the form survives; in kiosk mode (a
 * Guided-Access tablet that must never leave the sheet) the phrases are
 * plain text — the host has printed copies.
 */
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { PATHS } from '@/routes/paths';

interface SmsConsentCheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Optional override for the field name (useful inside larger forms). */
    name?: string;
    /** Compact rendering — smaller padding, used inside the chat widget
     *  where vertical space is tight. */
    dense?: boolean;
    /** Kiosk rendering — 56 px touch row, 24 px box and readable copy
     *  for the open-house tablet at arm's length; the policy phrases are
     *  plain text so the tablet can never leave the sheet. Wins over `dense`. */
    kiosk?: boolean;
    /** Server-provided consent wording shown verbatim instead of the fixed copy. */
    text?: string;
}

interface ConsentLayoutClasses {
    label: string;
    input: string;
    copy: string;
}

const DEFAULT_LAYOUT: ConsentLayoutClasses = { label: 'items-start gap-2.5 p-3', input: 'mt-0.5 w-4 h-4', copy: 'text-[11px]' };
const DENSE_LAYOUT: ConsentLayoutClasses = { ...DEFAULT_LAYOUT, label: 'items-start gap-2.5 p-2' };
const KIOSK_LAYOUT: ConsentLayoutClasses = { label: 'min-h-14 items-center gap-4 p-4', input: 'w-6 h-6', copy: 'text-base' };

function layoutFor(dense: boolean, kiosk: boolean): ConsentLayoutClasses {
    if (kiosk) return KIOSK_LAYOUT;
    return dense ? DENSE_LAYOUT : DEFAULT_LAYOUT;
}

const PRIVACY_PHRASE = 'Privacy Policy';
const TERMS_PHRASE = 'Terms';
const LINK_CLASS = 'text-primary hover:underline';
const KIOSK_PHRASE_CLASS = 'font-medium text-gray-700';
const LEGAL_PHRASE_PATTERN = new RegExp(`(${PRIVACY_PHRASE}|${TERMS_PHRASE})`);

interface LegalPhraseProps {
    phrase: string;
    href: string;
    /** Plain text on the kiosk — a Guided-Access tablet must never leave the sheet. */
    kiosk: boolean;
}

/** A policy reference that never navigates the form away: a new tab, or plain text on the kiosk. */
function LegalPhrase({ phrase, href, kiosk }: LegalPhraseProps) {
    if (kiosk) return <span className={KIOSK_PHRASE_CLASS}>{phrase}</span>;
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
            {phrase}
        </a>
    );
}

function PrivacyLink({ kiosk }: { kiosk: boolean }) {
    return <LegalPhrase phrase={PRIVACY_PHRASE} href={PATHS.PRIVACY} kiosk={kiosk} />;
}

function TermsLink({ kiosk }: { kiosk: boolean }) {
    return <LegalPhrase phrase={TERMS_PHRASE} href={PATHS.TERMS} kiosk={kiosk} />;
}

/** The server text with its "Privacy Policy" / "Terms" phrases linked. */
function renderConsentText(text: string, kiosk: boolean): ReactNode {
    const parts = text.split(LEGAL_PHRASE_PATTERN);
    const hasLegalLinks = parts.length > 1;
    return (
        <>
            {parts.map((part, index) => (
                <Fragment key={index}>
                    {part === PRIVACY_PHRASE
                        ? <PrivacyLink kiosk={kiosk} />
                        : part === TERMS_PHRASE ? <TermsLink kiosk={kiosk} /> : part}
                </Fragment>
            ))}
            {!hasLegalLinks && (
                <> See our <PrivacyLink kiosk={kiosk} /> and <TermsLink kiosk={kiosk} />.</>
            )}
        </>
    );
}

function DefaultConsentText({ kiosk }: { kiosk: boolean }) {
    return (
        <>
            I agree to receive SMS messages and phone calls from Real Estate with Shayaan
            at the number provided, including via automated technology. Consent is not a
            condition of purchase. Msg frequency varies. Message &amp; data rates may apply.
            Reply <span className="font-semibold">STOP</span> to unsubscribe,{' '}
            <span className="font-semibold">HELP</span> for help. See our{' '}
            <PrivacyLink kiosk={kiosk} />
            {' '}and{' '}
            <TermsLink kiosk={kiosk} />.
        </>
    );
}

export function SmsConsentCheckbox({
    checked,
    onChange,
    disabled = false,
    name = 'sms_consent',
    dense = false,
    kiosk = false,
    text,
}: SmsConsentCheckboxProps) {
    const layout = layoutFor(dense, kiosk);
    return (
        <label
            className={`flex ${layout.label} border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors`}
        >
            <input
                type="checkbox"
                name={name}
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                className={`${layout.input} flex-shrink-0 rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
            />
            <span className={`${layout.copy} text-gray-600 leading-relaxed`}>
                {text ? renderConsentText(text, kiosk) : <DefaultConsentText kiosk={kiosk} />}
            </span>
        </label>
    );
}

export default SmsConsentCheckbox;
